import { createAsyncThunk } from '@reduxjs/toolkit'
import { realtimeManager } from '../../utils/realtimeManager'
import { realtimeAPI } from '../../utils/supabase'
import { isOptimisticTaskMatch } from '../../utils/taskMatchingUtils'

// Realtime connection state management
export const initializeRealtimeSubscriptions = createAsyncThunk(
  'tasks/initializeRealtimeSubscriptions',
  async ({ userId, forceFresh = false }, { dispatch, rejectWithValue }) => {
    try {
      console.log('[Realtime] Initializing subscriptions for user:', userId, 'forceFresh:', forceFresh)

      // Initialize connection for user with reuse detection
      const connectionResult = realtimeManager.initializeForUser(userId, forceFresh)

      if (connectionResult.reused) {
        console.log('[Realtime] Attempting to reuse existing connection')

        // CRITICAL FIX: Fail fast if reuse claims success but has empty subscriptions
        if (connectionResult.subscriptions.length === 0) {
          console.warn('[Realtime] Connection marked as reused but has 0 subscriptions - forcing fresh initialization')
          realtimeManager.resetState()
          realtimeManager.currentUserId = userId
          // Fall through to fresh initialization below
        } else {
          // Perform immediate health check to validate the connection
          const isHealthy = realtimeManager.validateConnectionHealth()
          const actualConnectionState = realtimeManager.getConnectionState()

          // Set up connection state change listener to sync with Redux (if not already set)
          if (!realtimeManager.connectionUnsubscriber) {
            const unsubscribeConnectionListener = realtimeManager.onConnectionStateChange((isConnected) => {
              const connectionState = realtimeManager.getConnectionState()
              dispatch({ type: 'tasks/setRealtimeConnectionState', payload: connectionState })

              if (!isConnected && connectionState === 'ERROR') {
                dispatch({ type: 'tasks/setRealtimeError', payload: 'Connection lost - attempting to reconnect...' })
              } else if (isConnected) {
                dispatch({ type: 'tasks/clearRealtimeError' })
              }
            })

            realtimeManager.connectionUnsubscriber = unsubscribeConnectionListener
          }

          // If connection is not healthy, fall through to fresh initialization
          if (!isHealthy) {
            console.warn('[Realtime] Health check failed on reused connection, forcing fresh initialization')
            // Reset the connection state and continue with fresh setup
            realtimeManager.resetState()
            realtimeManager.currentUserId = userId
          } else {
            console.log('[Realtime] Reused connection is healthy with', connectionResult.subscriptions.length, 'subscriptions')
            // Connection is healthy, return the validated state
            return {
              subscriptions: connectionResult.subscriptions,
              connectionState: actualConnectionState, // Use actual validated state
              reused: true
            }
          }
        }
      }

      // If not reusing, continue with fresh initialization
      console.log('[Realtime] Setting up fresh subscriptions')

      // CRITICAL FIX: Set access token for realtime BEFORE creating subscriptions
      // This ensures RLS policies can authenticate events from realtime channels
      const authResult = await realtimeAPI.setAuthFromSession()
      if (!authResult.success) {
        console.error('[Realtime] Failed to set auth token, subscriptions may not receive events')
        return rejectWithValue('Failed to authenticate realtime connection')
      }
      console.log('[Realtime] Successfully set access token for realtime')

      // Set up connection state change listener to sync with Redux
      const unsubscribeConnectionListener = realtimeManager.onConnectionStateChange((isConnected) => {
        const connectionState = realtimeManager.getConnectionState()
        dispatch({ type: 'tasks/setRealtimeConnectionState', payload: connectionState })

        if (!isConnected && connectionState === 'ERROR') {
          dispatch({ type: 'tasks/setRealtimeError', payload: 'Connection lost - attempting to reconnect...' })
        } else if (isConnected) {
          dispatch({ type: 'tasks/clearRealtimeError' })
        }
      })

      // Store the unsubscribe function for cleanup
      realtimeManager.connectionUnsubscriber = unsubscribeConnectionListener

      // Tasks subscription
      realtimeManager.subscribe('tasks', {
        table: 'tasks',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          console.log('[Realtime] RAW task subscription callback received:', payload)
          console.log('[Realtime] Task event type from callback:', payload.eventType)
          dispatch(handleRealtimeTaskEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Tasks subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Tasks subscription error: ${error.message}` })
        }
      })

      // Projects subscription  
      realtimeManager.subscribe('projects', {
        table: 'projects',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          dispatch(handleRealtimeProjectEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Projects subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Projects subscription error: ${error.message}` })
        }
      })

      // Task statuses subscription
      realtimeManager.subscribe('task_statuses', {
        table: 'task_statuses',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          dispatch(handleRealtimeTaskStatusEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Task statuses subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Task statuses subscription error: ${error.message}` })
        }
      })

      // Board columns subscription
      realtimeManager.subscribe('board_columns', {
        table: 'board_columns',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          dispatch(handleRealtimeBoardColumnEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Board columns subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Board columns subscription error: ${error.message}` })
        }
      })

      // Column status mappings subscription
      realtimeManager.subscribe('column_status_mappings', {
        table: 'column_status_mappings',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          dispatch(handleRealtimeColumnMappingEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Column mappings subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Column mappings subscription error: ${error.message}` })
        }
      })

      // Task labels subscription
      realtimeManager.subscribe('task_labels', {
        table: 'task_labels',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          dispatch(handleRealtimeLabelEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Task labels subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Task labels subscription error: ${error.message}` })
        }
      })

      // Task label assignments subscription (for updating task.labels arrays)
      realtimeManager.subscribe('task_label_assignments', {
        table: 'task_label_assignments',
        filter: `user_id=eq.${userId}`,  // Filter events by user (required for event delivery)
        callback: (payload) => {
          dispatch(handleRealtimeLabelAssignmentEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Task label assignments subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Task label assignments subscription error: ${error.message}` })
        }
      })

      // Task attachments subscription (for multi-device file sync)
      realtimeManager.subscribe('task_attachments', {
        table: 'task_attachments',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          if (import.meta.env.DEV) {
            console.log('[Realtime] RAW attachment subscription callback received:', payload)
            console.log('[Realtime] Attachment event type from callback:', payload.eventType)
          }
          dispatch(handleRealtimeAttachmentEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Task attachments subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Task attachments subscription error: ${error.message}` })
        }
      })

      // Loops subscription (for cross-tab sync of loops)
      realtimeManager.subscribe('loops', {
        table: 'loops',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          if (import.meta.env.DEV) {
            console.log('[Realtime] Loops event received:', payload)
          }
          dispatch(handleRealtimeLoopEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Loops subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Loops subscription error: ${error.message}` })
        }
      })

      // Wiki spaces subscription (for cross-tab sync of wiki spaces)
      realtimeManager.subscribe('wiki_spaces', {
        table: 'wiki_spaces',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          if (import.meta.env.DEV) {
            console.log('[Realtime] Wiki spaces event received:', payload)
          }
          dispatch(handleRealtimeWikiSpaceEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Wiki spaces subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Wiki spaces subscription error: ${error.message}` })
        }
      })

      // Wiki pages subscription (for cross-tab sync of wiki pages)
      realtimeManager.subscribe('wiki_pages', {
        table: 'wiki_pages',
        filter: `user_id=eq.${userId}`,
        callback: (payload) => {
          if (import.meta.env.DEV) {
            console.log('[Realtime] Wiki pages event received:', payload)
          }
          dispatch(handleRealtimeWikiPageEvent(payload))
        },
        errorCallback: (error) => {
          console.error('[Realtime] Wiki pages subscription error:', error)
          dispatch({ type: 'tasks/setRealtimeError', payload: `Wiki pages subscription error: ${error.message}` })
        }
      })

      // Start health monitoring for new connections
      realtimeManager.startHealthMonitoring()

      return { 
        subscriptions: realtimeManager.getActiveSubscriptions(),
        connectionState: realtimeManager.getConnectionState()
      }
    } catch (error) {
      console.error('[Realtime] Failed to initialize subscriptions:', error)
      return rejectWithValue(error.message)
    }
  }
)

// Clean up all realtime subscriptions
export const cleanupRealtimeSubscriptions = createAsyncThunk(
  'tasks/cleanupRealtimeSubscriptions',
  async (_, { rejectWithValue }) => {
    try {
      // Clean up connection state listener if it exists
      if (realtimeManager.connectionUnsubscriber) {
        realtimeManager.connectionUnsubscriber()
        realtimeManager.connectionUnsubscriber = null
      }
      
      // Clean up all realtime subscriptions
      realtimeManager.unsubscribeAll()
      
      return { success: true }
    } catch (error) {
      console.error('[Realtime] Failed to cleanup subscriptions:', error)
      return rejectWithValue(error.message)
    }
  }
)

// Individual event handlers
export const handleRealtimeTaskEvent = createAsyncThunk(
  'tasks/handleRealtimeTaskEvent',
  async (payload) => {
    console.log('[Realtime] Received task event:', payload)
    console.log('[Realtime] Full payload keys:', Object.keys(payload))
    
    // Try both possible event type properties
    const eventType = payload.eventType || payload.event
    const newRecord = payload.new || payload.record
    const oldRecord = payload.old || payload.old_record
    
    console.log('[Realtime] Task event details:', {
      eventType,
      newRecord,
      oldRecord,
      hasNewRecord: !!newRecord,
      hasOldRecord: !!oldRecord,
      payloadEventType: payload.eventType,
      payloadEvent: payload.event
    })
    
    switch (eventType) {
      case 'INSERT':
        console.log('[Realtime] Processing task INSERT:', newRecord)
        // Task was created - add to state
        return { type: 'INSERT', record: newRecord }
        
      case 'UPDATE':
        console.log('[Realtime] Processing task UPDATE:', { newRecord, oldRecord })
        // Task was updated - update in state
        return { type: 'UPDATE', record: newRecord, oldRecord }
        
      case 'DELETE': {
        console.log('[Realtime] Processing task DELETE:', oldRecord)
        // Task was deleted - remove from state
        // Use oldRecord for DELETE events, but fall back to newRecord if oldRecord is undefined
        const deleteRecord = oldRecord || newRecord
        return { type: 'DELETE', record: deleteRecord }
      }
        
      default:
        console.warn('[Realtime] Unknown task event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeProjectEvent = createAsyncThunk(
  'tasks/handleRealtimeProjectEvent',
  async (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload
    
    switch (eventType) {
      case 'INSERT':
        return { type: 'INSERT', record: newRecord }
        
      case 'UPDATE':
        return { type: 'UPDATE', record: newRecord, oldRecord }
        
      case 'DELETE':
        return { type: 'DELETE', record: oldRecord || newRecord }
        
      default:
        console.warn('[Realtime] Unknown project event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeTaskStatusEvent = createAsyncThunk(
  'tasks/handleRealtimeTaskStatusEvent',
  async (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload
    
    switch (eventType) {
      case 'INSERT':
        return { type: 'INSERT', record: newRecord }
        
      case 'UPDATE':
        return { type: 'UPDATE', record: newRecord, oldRecord }
        
      case 'DELETE':
        return { type: 'DELETE', record: oldRecord }
        
      default:
        console.warn('[Realtime] Unknown task status event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeBoardColumnEvent = createAsyncThunk(
  'tasks/handleRealtimeBoardColumnEvent',
  async (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload
    
    switch (eventType) {
      case 'INSERT':
        return { type: 'INSERT', record: newRecord }
        
      case 'UPDATE':
        return { type: 'UPDATE', record: newRecord, oldRecord }
        
      case 'DELETE':
        return { type: 'DELETE', record: oldRecord }
        
      default:
        console.warn('[Realtime] Unknown board column event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeColumnMappingEvent = createAsyncThunk(
  'tasks/handleRealtimeColumnMappingEvent',
  async (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload

    switch (eventType) {
      case 'INSERT':
        return { type: 'INSERT', record: newRecord }

      case 'UPDATE':
        return { type: 'UPDATE', record: newRecord, oldRecord }

      case 'DELETE':
        return { type: 'DELETE', record: oldRecord }

      default:
        console.warn('[Realtime] Unknown column mapping event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeLabelEvent = createAsyncThunk(
  'labels/handleRealtimeLabelEvent',
  async (payload) => {
    // Handle both old and new Supabase realtime payload formats
    const eventData = payload.data || payload
    const eventType = eventData.type || eventData.eventType
    const newRecord = eventData.record || eventData.new
    const oldRecord = eventData.old_record || eventData.old

    if (import.meta.env.DEV) {
      console.log('[Realtime] Label event:', { eventType, newRecord, oldRecord })
    }

    switch (eventType) {
      case 'INSERT':
        return { type: 'INSERT', record: newRecord }

      case 'UPDATE':
        if (import.meta.env.DEV) {
          console.log('[Realtime] Label UPDATE - updating tasks with label:', newRecord)
        }
        return { type: 'UPDATE', record: newRecord, oldRecord }

      case 'DELETE':
        return { type: 'DELETE', record: oldRecord }

      default:
        console.warn('[Realtime] Unknown label event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeLabelAssignmentEvent = createAsyncThunk(
  'labels/handleRealtimeLabelAssignmentEvent',
  async (payload, { getState }) => {
    // Handle both old and new Supabase realtime payload formats
    // New format wraps data in payload.data, old format has direct properties
    const eventData = payload.data || payload
    const eventType = eventData.type || eventData.eventType
    const newRecord = eventData.record || eventData.new
    const oldRecord = eventData.old_record || eventData.old

    const state = getState()

    // For label assignments, we need to fetch the full label data
    // to update the task's labels array properly
    const labels = state.labels?.labels || []

    switch (eventType) {
      case 'INSERT': {
        // Find the label being assigned
        const label = labels.find(l => l.id === newRecord.label_id)
        return {
          type: 'INSERT',
          assignmentId: newRecord.id,  // Cache this for DELETE lookups
          taskId: newRecord.task_id,
          labelId: newRecord.label_id,
          label
        }
      }

      case 'DELETE': {
        // Label removed from task - use cache to lookup task_id and label_id
        const assignmentId = oldRecord?.id || newRecord?.id

        // Get assignment from cache
        const cachedAssignment = state.tasks?.labelAssignments?.[assignmentId]

        if (!cachedAssignment) {
          console.error('[Realtime] DELETE failed - assignment not in cache:', assignmentId)
          console.error('[Realtime] This label was likely added before realtime was enabled.')
          console.error('[Realtime] Cannot remove label without task_id and label_id.')
        }

        return {
          type: 'DELETE',
          assignmentId,
          taskId: cachedAssignment?.task_id,
          labelId: cachedAssignment?.label_id
        }
      }

      default:
        console.warn('[Realtime] Unknown label assignment event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeAttachmentEvent = createAsyncThunk(
  'attachments/handleRealtimeAttachmentEvent',
  async (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload

    switch (eventType) {
      case 'INSERT':
        return { type: 'INSERT', record: newRecord }

      case 'UPDATE':
        return { type: 'UPDATE', record: newRecord, oldRecord }

      case 'DELETE':
        return { type: 'DELETE', record: oldRecord || newRecord }

      default:
        console.warn('[Realtime] Unknown attachment event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeLoopEvent = createAsyncThunk(
  'loops/handleRealtimeLoopEvent',
  async (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload

    switch (eventType) {
      case 'INSERT':
        return { type: 'INSERT', record: newRecord }

      case 'UPDATE':
        return { type: 'UPDATE', record: newRecord, oldRecord }

      case 'DELETE':
        return { type: 'DELETE', record: oldRecord || newRecord }

      default:
        console.warn('[Realtime] Unknown loop event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeWikiSpaceEvent = createAsyncThunk(
  'wiki/handleRealtimeWikiSpaceEvent',
  async (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload

    switch (eventType) {
      case 'INSERT':
        return { type: 'INSERT', record: newRecord }

      case 'UPDATE':
        return { type: 'UPDATE', record: newRecord, oldRecord }

      case 'DELETE':
        return { type: 'DELETE', record: oldRecord || newRecord }

      default:
        console.warn('[Realtime] Unknown wiki space event type:', eventType)
        return null
    }
  }
)

export const handleRealtimeWikiPageEvent = createAsyncThunk(
  'wiki/handleRealtimeWikiPageEvent',
  async (payload) => {
    const { eventType, new: newRecord, old: oldRecord } = payload

    switch (eventType) {
      case 'INSERT':
        return { type: 'INSERT', record: newRecord }

      case 'UPDATE':
        return { type: 'UPDATE', record: newRecord, oldRecord }

      case 'DELETE':
        return { type: 'DELETE', record: oldRecord || newRecord }

      default:
        console.warn('[Realtime] Unknown wiki page event type:', eventType)
        return null
    }
  }
)

// Realtime reducers for handling live updates
export const realtimeReducers = {
  // Initialize realtime subscriptions
  [initializeRealtimeSubscriptions.pending]: (state) => {
    state.realtime = {
      ...state.realtime,
      initializing: true,
      error: null
    }
  },
  [initializeRealtimeSubscriptions.fulfilled]: (state, action) => {
    // CRITICAL FIX: Don't mark as initialized if we have no subscriptions
    const subscriptions = action.payload.subscriptions || []
    const hasSubscriptions = subscriptions.length > 0

    if (!hasSubscriptions) {
      console.error('[Realtime] Initialization completed but no subscriptions were created - marking as failed')
      state.realtime = {
        ...state.realtime,
        initializing: false,
        initialized: false,
        subscriptions: [],
        connectionState: 'ERROR',
        error: 'Failed to create subscriptions'
      }
    } else {
      console.log('[Realtime] Successfully initialized with', subscriptions.length, 'subscriptions')
      state.realtime = {
        ...state.realtime,
        initializing: false,
        initialized: true,
        subscriptions: subscriptions,
        connectionState: action.payload.connectionState,
        error: null
      }
    }
  },
  [initializeRealtimeSubscriptions.rejected]: (state, action) => {
    state.realtime = {
      ...state.realtime,
      initializing: false,
      initialized: false,
      error: action.payload,
      connectionState: 'ERROR'
    }
  },

  // Cleanup realtime subscriptions
  [cleanupRealtimeSubscriptions.fulfilled]: (state) => {
    state.realtime = {
      initializing: false,
      initialized: false,
      subscriptions: [],
      connectionState: 'DISCONNECTED',
      error: null
    }
  },

  // Handle realtime task events
  [handleRealtimeTaskEvent.fulfilled]: (state, action) => {
    if (!action.payload) {
      return
    }

    const { type, record } = action.payload

    switch (type) {
      case 'INSERT':
        // Skip if this task was just created optimistically by this client
        // Uses shared matching logic to avoid code duplication
        const isOwnOptimisticTask = state.tasks.some(task =>
          isOptimisticTaskMatch(task, record)
        )

        if (isOwnOptimisticTask) {
          if (import.meta.env.DEV) {
            console.log('[Realtime] Skipping own optimistic task INSERT - will be replaced by fulfilled action')
          }
          break
        }

        // Add new task (avoid duplicates by ID)
        if (!state.tasks.find(task => task.id === record.id)) {
          state.tasks.unshift(record)
        }
        break

      case 'UPDATE': {
        // Update existing task
        const taskIndex = state.tasks.findIndex(task => task.id === record.id)
        if (taskIndex !== -1) {
          // Preserve labels array from existing task if not in update payload
          // (Realtime sends raw task record without labels join)
          const existingTask = state.tasks[taskIndex]
          state.tasks[taskIndex] = {
            ...existingTask,
            ...record,
            // Preserve labels if not explicitly provided in update
            labels: record.labels !== undefined ? record.labels : existingTask.labels
          }
        }
        break
      }

      case 'DELETE': {
        // Use the record we set up in the event handler (could be oldRecord or newRecord fallback)
        if (record?.id) {
          // Clean up label assignment cache for this task
          if (state.labelAssignments) {
            Object.keys(state.labelAssignments).forEach(assignmentId => {
              if (state.labelAssignments[assignmentId].task_id === record.id) {
                delete state.labelAssignments[assignmentId]
              }
            })
          }

          state.tasks = state.tasks.filter(task => task.id !== record.id)
        }
        break
      }
        
      default:
        console.warn('[Realtime] Unknown task event type in reducer:', type)
    }
  },

  // Handle realtime project events
  [handleRealtimeProjectEvent.fulfilled]: (state, action) => {
    if (!action.payload) return

    const { type, record, oldRecord } = action.payload

    switch (type) {
      case 'INSERT':
        if (!state.projects.find(project => project.id === record.id)) {
          state.projects.push(record)
          // Sort projects by display_order after insertion
          state.projects.sort((a, b) => a.display_order - b.display_order)
        }
        break

      case 'UPDATE': {
        const projectIndex = state.projects.findIndex(project => project.id === record.id)
        if (projectIndex !== -1) {
          const oldProject = state.projects[projectIndex]
          state.projects[projectIndex] = { ...oldProject, ...record }
          
          // Check if display_order changed and sort if necessary
          if (oldProject.display_order !== record.display_order) {
            state.projects.sort((a, b) => a.display_order - b.display_order)
          }
        }
        break
      }

      case 'DELETE':
        // Use oldRecord if available, otherwise fall back to record for the ID
        const projectToDelete = oldRecord || record
        if (projectToDelete?.id) {
          state.projects = state.projects.filter(project => project.id !== projectToDelete.id)
        }
        break
    }
  },

  // Handle realtime task status events
  [handleRealtimeTaskStatusEvent.fulfilled]: (state, action) => {
    if (!action.payload) return

    const { type, record, oldRecord } = action.payload

    switch (type) {
      case 'INSERT':
        if (!state.taskStatuses.find(status => status.id === record.id)) {
          state.taskStatuses.push(record)
        }
        break

      case 'UPDATE': {
        const statusIndex = state.taskStatuses.findIndex(status => status.id === record.id)
        if (statusIndex !== -1) {
          state.taskStatuses[statusIndex] = { ...state.taskStatuses[statusIndex], ...record }
        }
        break
      }

      case 'DELETE':
        // Use oldRecord if available, otherwise fall back to record for the ID
        const statusToDelete = oldRecord || record
        if (statusToDelete?.id) {
          state.taskStatuses = state.taskStatuses.filter(status => status.id !== statusToDelete.id)
        }
        break
    }
  },

  // Handle realtime board column events
  [handleRealtimeBoardColumnEvent.fulfilled]: (state, action) => {
    if (!action.payload) return

    const { type, record, oldRecord } = action.payload

    switch (type) {
      case 'INSERT':
        if (!state.boardColumns.find(column => column.id === record.id)) {
          state.boardColumns.push(record)
        }
        break

      case 'UPDATE': {
        const columnIndex = state.boardColumns.findIndex(column => column.id === record.id)
        if (columnIndex !== -1) {
          state.boardColumns[columnIndex] = { ...state.boardColumns[columnIndex], ...record }
        }
        break
      }

      case 'DELETE':
        // Use oldRecord if available, otherwise fall back to record for the ID
        const columnToDelete = oldRecord || record
        if (columnToDelete?.id) {
          state.boardColumns = state.boardColumns.filter(column => column.id !== columnToDelete.id)
        }
        break
    }
  },

  // Handle realtime column mapping events
  [handleRealtimeColumnMappingEvent.fulfilled]: (state, action) => {
    if (!action.payload) return

    const { type, record, oldRecord } = action.payload

    switch (type) {
      case 'INSERT':
        if (!state.columnStatusMappings.find(mapping => mapping.id === record.id)) {
          state.columnStatusMappings.push(record)
        }
        break

      case 'UPDATE': {
        const mappingIndex = state.columnStatusMappings.findIndex(mapping => mapping.id === record.id)
        if (mappingIndex !== -1) {
          state.columnStatusMappings[mappingIndex] = { ...state.columnStatusMappings[mappingIndex], ...record }
        }
        break
      }

      case 'DELETE':
        // Use oldRecord if available, otherwise fall back to record for the ID
        const mappingToDelete = oldRecord || record
        if (mappingToDelete?.id) {
          state.columnStatusMappings = state.columnStatusMappings.filter(mapping => mapping.id !== mappingToDelete.id)
        }
        break
    }
  },

  // Handle realtime label assignment events (updates task.labels arrays)
  //
  // IMPORTANT: This reducer uses a cache strategy to work around Supabase Realtime limitations.
  // DELETE events only contain the assignment ID (primary key), not task_id or label_id.
  // Even with REPLICA IDENTITY FULL, Supabase only sends the primary key on DELETE.
  //
  // NOTE: REPLICA IDENTITY FULL is REQUIRED on task_label_assignments for Supabase
  // Realtime to broadcast DELETE events at all. Without it, remote tabs don't receive
  // DELETE events. This is a Supabase-specific requirement (migration 051).
  //
  // Strategy:
  // 1. On INSERT: Cache the assignment { assignmentId: { task_id, label_id } }
  // 2. On DELETE: Lookup task_id and label_id from cache using assignmentId
  // 3. Cache is limited to 5000 entries with FIFO eviction to prevent memory leaks
  // 4. Cache is cleared when tasks are cleared (logout, etc.)
  //
  // Edge case: If DELETE event arrives before cache hydration completes (rare on
  // slow connections), the DELETE will fail silently. Cache is hydrated on page
  // load before Realtime connects, minimizing this risk.
  [handleRealtimeLabelAssignmentEvent.fulfilled]: (state, action) => {
    if (!action.payload) {
      return
    }

    const { type, assignmentId, taskId, labelId, label } = action.payload

    switch (type) {
      case 'INSERT': {
        // Validate required fields before caching
        if (!assignmentId || !taskId || !labelId) {
          if (import.meta.env.DEV) {
            console.warn('[Realtime] Missing required IDs for cache:', { assignmentId, taskId, labelId })
          }
          break
        }

        if (!state.labelAssignments) {
          state.labelAssignments = {}
        }

        // Prevent unbounded cache growth - limit to 5000 entries (FIFO eviction)
        // Note: This is insertion-order eviction, not true LRU (no access tracking)
        // Memory footprint: ~100KB for 5000 entries (negligible)
        const cacheSize = Object.keys(state.labelAssignments).length
        if (cacheSize >= 5000) {
          // Keep most recent 2500 entries based on insertion order
          const entries = Object.entries(state.labelAssignments)
          state.labelAssignments = Object.fromEntries(entries.slice(-2500))
          if (import.meta.env.DEV) {
            console.log('[Realtime] Cache size limit reached, evicted old entries')
          }
        }

        // Cache the assignment for future DELETE lookups
        state.labelAssignments[assignmentId] = { task_id: taskId, label_id: labelId }

        // Add label to task's labels array
        const taskIndex = state.tasks.findIndex(task => task.id === taskId)

        if (taskIndex !== -1 && label) {
          const task = state.tasks[taskIndex]

          if (!task.labels) {
            task.labels = []
          }
          // Avoid duplicates
          if (!task.labels.find(l => l.id === labelId)) {
            task.labels.push(label)
          }
        }
        break
      }

      case 'DELETE': {
        // Remove label from task's labels array
        if (taskId && labelId) {
          const taskIndex = state.tasks.findIndex(task => task.id === taskId)

          if (taskIndex !== -1) {
            const task = state.tasks[taskIndex]
            if (task.labels) {
              task.labels = task.labels.filter(l => l.id !== labelId)
            }
          }

          // Remove from cache
          if (assignmentId && state.labelAssignments) {
            delete state.labelAssignments[assignmentId]
          }
        } else if (import.meta.env.DEV) {
          console.warn('[Realtime] Cannot remove label - missing taskId or labelId:', { taskId, labelId })
        }
        break
      }
    }
  },

  // Handle realtime label events (label definition changes like rename)
  [handleRealtimeLabelEvent.fulfilled]: (state, action) => {
    if (!action.payload) {
      return
    }

    const { type, record } = action.payload

    switch (type) {
      case 'UPDATE': {
        // Label was renamed or color changed - update all tasks with this label
        if (record && record.id) {
          state.tasks.forEach(task => {
            if (task.labels && task.labels.length > 0) {
              const labelIndex = task.labels.findIndex(l => l.id === record.id)
              if (labelIndex !== -1) {
                task.labels[labelIndex] = {
                  ...task.labels[labelIndex],
                  name: record.name,
                  color: record.color,
                  description: record.description
                }
              }
            }
          })
        }
        break
      }

      case 'DELETE': {
        // Label was deleted - remove from all tasks and clean up cache
        if (record && record.id) {
          // Remove label from all tasks
          state.tasks.forEach(task => {
            if (task.labels && task.labels.length > 0) {
              task.labels = task.labels.filter(l => l.id !== record.id)
            }
          })

          // Clean up cache entries for this label
          if (state.labelAssignments) {
            Object.keys(state.labelAssignments).forEach(assignmentId => {
              if (state.labelAssignments[assignmentId].label_id === record.id) {
                delete state.labelAssignments[assignmentId]
              }
            })
          }
        }
        break
      }

      case 'INSERT': {
        // New label created - no action needed for tasks
        break
      }
    }
  }
}

// Regular reducers for connection state management
export const realtimeRegularReducers = {
  setRealtimeConnectionState: (state, action) => {
    state.realtime = {
      ...state.realtime,
      connectionState: action.payload
    }
  },
  
  setRealtimeError: (state, action) => {
    state.realtime = {
      ...state.realtime,
      error: action.payload
    }
  },
  
  clearRealtimeError: (state) => {
    state.realtime = {
      ...state.realtime,
      error: null
    }
  }
}
